import React, { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const LineIntegration = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [channelAccessToken, setChannelAccessToken] = useState('');
  const [monthsBack, setMonthsBack] = useState(6);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const handleTestConnection = async () => {
    if (!channelAccessToken) {
      toast.error('Please enter Line Channel Access Token');
      return;
    }

    setLoading(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/integrations/line/test`, {
        channel_access_token: channelAccessToken
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Line API connection successful!');
      } else {
        toast.error('Line API connection failed');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed'
      });
      toast.error('Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!channelAccessToken) {
      toast.error('Please enter Line Channel Access Token');
      return;
    }

    setLoading(true);
    setImportResult(null);
    try {
      const response = await axios.post(`${API}/integrations/line/import`, {
        channel_access_token: channelAccessToken,
        months_back: monthsBack
      });
      setImportResult(response.data);
      if (response.data.success) {
        toast.success(`Imported ${response.data.imported_count} properties`);
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_thai-property-hub/artifacts/f44qgbfh_3.jpg" 
                alt="GDA Logo" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
              <div>
                <h1 className="font-display text-2xl font-bold text-amber-900">Line Integration</h1>
                <p className="text-sm text-teal-700 font-medium">Import properties from Line</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/admin/dashboard')} 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Instructions Card */}
        <Card className="mb-8 border-amber-200">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-amber-900">Setup Instructions</CardTitle>
            <CardDescription>Follow these steps to import properties from Line</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-amber-900">Step 1: Get Line API Credentials</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 ml-4">
                <li>Go to <a href="https://developers.line.biz/console/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Line Developers Console</a></li>
                <li>Create or select your Line Official Account</li>
                <li>Go to the "Messaging API" tab</li>
                <li>Issue a Channel Access Token (long-lived)</li>
              </ol>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-semibold text-amber-900">Step 2: Configure Integration</h3>
              <p className="text-sm text-gray-700">Enter your credentials below and test the connection</p>
            </div>

            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800">
                <strong>Important:</strong> Line Messaging API doesn't provide direct access to historical posts. 
                To import properties, you'll need to either:
                <ul className="list-disc list-inside mt-2 ml-4">
                  <li>Set up a webhook to capture posts in real-time going forward</li>
                  <li>Manually extract property data from your Line posts</li>
                  <li>Use Line Official Account Manager to export post data</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Configuration Form */}
        <Card className="border-teal-200">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-amber-900">Line API Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="channel-access-token">Channel Access Token *</Label>
                <Input
                  id="channel-access-token"
                  type="password"
                  value={channelAccessToken}
                  onChange={(e) => setChannelAccessToken(e.target.value)}
                  placeholder="Enter your Line Channel Access Token"
                  className="border-amber-200 focus:ring-teal-500"
                  data-testid="line-token-input"
                />
                <p className="text-xs text-gray-500 mt-1">Get this from Line Developers Console → Messaging API</p>
              </div>

              <div>
                <Label htmlFor="months-back">Import Period (Months)</Label>
                <Input
                  id="months-back"
                  type="number"
                  min="1"
                  max="12"
                  value={monthsBack}
                  onChange={(e) => setMonthsBack(parseInt(e.target.value))}
                  className="border-amber-200 focus:ring-teal-500"
                  data-testid="months-back-input"
                />
                <p className="text-xs text-gray-500 mt-1">Number of months to look back for posts</p>
              </div>
            </div>

            {/* Test Connection */}
            <div className="flex gap-3">
              <Button 
                onClick={handleTestConnection}
                disabled={loading}
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="test-connection-button"
              >
                {loading ? 'Testing...' : 'Test Connection'}
              </Button>
              
              <Button 
                onClick={handleImport}
                disabled={loading || !channelAccessToken}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800"
                data-testid="import-properties-button"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? 'Importing...' : 'Import Properties'}
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <Alert className={testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                  <strong>{testResult.success ? 'Success:' : 'Error:'}</strong> {testResult.message}
                  {testResult.bot_info && (
                    <div className="mt-2 text-sm">
                      <p>Bot Name: {testResult.bot_info.displayName}</p>
                      <p>User ID: {testResult.bot_info.userId}</p>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Import Result */}
            {importResult && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <strong>Import Result:</strong>
                  <div className="mt-2 space-y-1">
                    <p>✓ Imported: {importResult.imported_count} properties</p>
                    {importResult.failed_count > 0 && (
                      <p>✗ Failed: {importResult.failed_count} properties</p>
                    )}
                    <p className="text-sm mt-2">{importResult.message}</p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Alternative Method */}
        <Card className="mt-8 border-amber-200">
          <CardHeader>
            <CardTitle className="font-display text-xl text-amber-900">Alternative: Manual Import</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              If API integration is not working, you can manually import properties from your Line posts:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Export your Line post data (if available through Line Official Account Manager)</li>
              <li>Extract property information (title, description, price, images, etc.)</li>
              <li>Use the admin dashboard to manually create properties with this information</li>
              <li>Or contact support to help with bulk CSV import</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LineIntegration;
